import store from "@/store";
import axios from "axios";
import { Toast } from "vant";
import { Message } from "element-ui";
import router from "@/router";
// import apiConfig from "@/config/api";
import { isMobile } from "@/utils/device";
import { translate } from "@/i18n/catalog";
import { agentServiceBrowserErrorMessage } from "@/utils/agent-service/browser-error";

const uiText = (key) => translate(store.state.locale.uiLocale, key);

// ===================== 通用工具函数封装 =====================
// 显示加载提示（根据设备类型）
const showLoading = () => {
  if (isMobile()) {
    // 移动端：Vant Toast
    return Toast.loading({
      message: uiText("common.loading"),
      forbidClick: true,
      overlay: false,
      loadingType: "spinner",
      duration: 0, // 不自动关闭
    });
  } else {
    // PC端：Element Message
    // return Message({
    //   message: "加载中...",
    //   type: "info",
    //   duration: 0, // 不自动关闭
    //   showClose: false,
    // });
  }
};

// 关闭加载提示（根据设备类型）
const closeLoading = (loadingInstance) => {
  if (!loadingInstance) {
    return;
  }

  if (typeof loadingInstance.clear === "function") {
    loadingInstance.clear();
  } else if (typeof loadingInstance.close === "function") {
    loadingInstance.close();
  }
};

// 显示错误提示（根据设备类型）
const showError = (message) => {
  if (isMobile()) {
    Toast({
      message,
      type: "fail",
      duration: 2000,
    });
  } else {
    Message({
      message,
      type: "error",
      duration: 2000,
    });
  }
};

const isGoodsLibraryApiRequest = (config = {}) => {
  const url = config.url || "";
  return url.includes("/api/mts-api/goods/");
};

const shouldSkipAuthRedirect = (config = {}) => {
  return Boolean(config.skipAuthRedirect);
};

const shouldSkipErrorMessage = (config = {}) => {
  return Boolean(config.skipErrorMessage);
};

const openPcLoginDialog = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("open-login-dialog"));
  }
};

// 页面初始化通常会并行请求用户资料、余额、消息等接口。登录态失效时，
// 这些请求会在同一时刻全部返回 401；统一收口提示，避免叠出多条
// “请先登录”并重复触发登录弹窗。
const AUTH_PROMPT_COOLDOWN = 5000;
let lastAuthPromptAt = 0;

const shouldOpenAuthPrompt = () => {
  const now = Date.now();
  if (now - lastAuthPromptAt < AUTH_PROMPT_COOLDOWN) {
    return false;
  }
  lastAuthPromptAt = now;
  return true;
};

// 处理401错误（移动端跳登录页，PC端打开登录弹窗）
const handle401 = (config = {}) => {
  if (shouldSkipAuthRedirect(config)) {
    return;
  }
  if (!config.priceChannelRawResponse) closeLoading(config.loadingInstance); // 只关闭当前请求的加载提示
  // const errorMsg = "身份验证失败，请重新登录";
  // 显示错误提示
  // isMobile() ? Toast.fail(errorMsg) : Message.error(errorMsg);
  if (!isMobile()) {
    if (!shouldOpenAuthPrompt()) {
      return;
    }
    Message.warning(uiText("common.loginRequired"));
    openPcLoginDialog();
    return;
  }

  if (!shouldOpenAuthPrompt()) {
    return;
  }

  const loginPath = "/login";
  setTimeout(() => {
    // 延迟1秒后跳转，避免短暂闪烁
    router.push({
      path: loginPath,
      query: { backUrl: router.currentRoute.fullPath },
    });
  }, 2000);
  // if (isMobile()) {
  //   const loginPath = isMobile() ? "/login" : "/pc/home";
  //   setTimeout(() => {
  //     // 延迟1秒后跳转，避免短暂闪烁
  //     router.push({
  //       path: loginPath,
  //       query: { backUrl: router.currentRoute.fullPath },
  //     });
  //   }, 1000);
  // }
};

// 通用请求拦截器（适用于所有实例）
const commonRequestInterceptor = (config) => {
  // 显示加载提示
  config.loadingInstance = config.skipLoading || config.priceChannelRawResponse ? null : showLoading();
  // 当前站点界面语言只作为 HTTP 展示偏好；会话消息的语言快照另行管理。
  config.headers = config.headers || {};
  if (!config.headers["Accept-Language"]) {
    config.headers["Accept-Language"] = store.state.locale.uiLocale;
  }

  // 携带token
  const token = store.getters.token;
  if (token) {
    config.headers["Authorization"] = token;
    config.headers.platform = isMobile() ? "H5" : "PC"; // 动态平台标识
  }

  // 流式请求日志
  const isStreamRequest =
    config.headers &&
    (config.headers["Accept"] === "text/event-stream" ||
      config.url?.includes("stream"));
  if (isStreamRequest) {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`🌊 [${timestamp}] SSE流式请求:`, config.url);
  }

  return config;
};

// 通用响应拦截器（适用于所有实例）
const commonResponseInterceptor = (response) => {
  // 关闭加载提示
  if (!response.config || !response.config.priceChannelRawResponse)
    closeLoading(response.config && response.config.loadingInstance);

  // 价格频道需读取 ETag、Release 响应头，并把 304 交给局部缓存恢复。
  // 仅显式 opt-in 的请求保留 Axios 响应，其余业务仍沿用原有解包约定。
  if (response.config && response.config.priceChannelRawResponse) {
    return response;
  }

  const res = response.data;
  if (response.data instanceof Blob) {
    if (response.data.size == 0) {
      showError(uiText("common.downloadFailed"));
    }
  } else if (res.code !== 200) {
    if (res.code === 401) {
      if (shouldSkipErrorMessage(response.config)) {
        // 公开搜索、登录和注册由页面展示上下文更准确的错误信息。
      } else if (isGoodsLibraryApiRequest(response.config)) {
        showError(res.msg || uiText("common.unauthorizedGoods"));
      } else if (shouldSkipAuthRedirect(response.config)) {
        // 登录接口自身返回未授权时只展示接口错误，不能再次打开登录页。
        showError(res.msg || uiText("devSync.loginFailedCheckYourAccountDetails"));
      } else {
        handle401(response.config); // 处理401错误
      }
    } else if (shouldSkipErrorMessage(response.config)) {
      // 页面自行处理业务错误，避免全局提示与表单提示重复出现。
    } else if (res.code === 500) {
      showError(res.msg || uiText("common.serverError"));
    } else {
      showError(res.msg || uiText("common.operationFailed"));
    }
    return res;
  }
  return res;
};

// 通用错误拦截器（适用于所有实例）
const commonErrorInterceptor = (error) => {
  // 关闭加载提示
  if (!error.config || !error.config.priceChannelRawResponse) closeLoading(error.config && error.config.loadingInstance);

  // 价格频道的非 2xx 错误由局部 Adapter 按稳定错误码处理；仅 401 复用
  // 站内登录流程，取消的旧请求不弹出全站错误提示。
  if (error.config && error.config.priceChannelRawResponse) {
    if (error.response && error.response.status === 401)
      handle401(error.config);
    return Promise.reject(error);
  }

  // 看板仅显式接管错误显示，避免先弹Axios英文原文、再弹页面三语提示。
  // 401仍沿用原登录处理，不把国际化适配变成绕过登录或吞掉失败。
  if (error.config && error.config.reportDashboardRawError) {
    if (error.response && error.response.status === 401)
      handle401(error.config);
    return Promise.reject(error);
  }

  // 仅已接双语契约的客服 REST 错误使用本次请求的服务端译文；不改写聊天正文或翻译状态。
  const agentServiceMessage = agentServiceBrowserErrorMessage(error);
  if (agentServiceMessage) error.message = agentServiceMessage;

  if (error.response && error.response.status === 401) {
    if (shouldSkipErrorMessage(error.config)) {
      // 页面自行处理。
    } else if (isGoodsLibraryApiRequest(error.config)) {
      showError(uiText("common.unauthorizedGoods"));
    } else if (shouldSkipAuthRedirect(error.config)) {
      showError(
        error.response.data?.msg || uiText("devSync.loginFailedCheckYourAccountDetails"),
      );
    } else {
      handle401(error.config); // 处理401错误
    }
  } else if (!shouldSkipErrorMessage(error.config)) {
    // 其他错误提示
    let errorMsg = uiText("common.requestError");
    if (error.code === "ECONNABORTED" && error.message.includes("timeout")) {
      errorMsg = uiText("common.requestTimeout");
    } else if (error.message) {
      errorMsg = error.message;
    }
    const messages = (error.config && error.config.errorMessages) || {};
    showError(messages[error.response && error.response.status] || errorMsg);
  }
  return Promise.reject(error);
};

// ===================== 实例创建与配置 =====================
const configuredApiTimeout = Number(process.env.VUE_APP_API_TIMEOUT);
const apiTimeout = Number.isFinite(configuredApiTimeout) && configuredApiTimeout > 0
  ? configuredApiTimeout
  : 10000;
// 创建默认的请求实例（用于常规API请求）
const instance = axios.create({
  baseURL: "/dev-api",
  timeout: apiTimeout,
  headers: { platform: isMobile() ? "H5" : "PC" },
});

// 创建Coze专用的请求实例（用于Coze API，不使用/dev-api前缀）
const cozeInstance = axios.create({
  baseURL: "/coze-api",
  timeout: 30000, // AI接口可能需要更长时间
  headers: { platform: isMobile() ? "H5" : "PC" },
});

// 配置默认实例的拦截器
instance.interceptors.request.use(commonRequestInterceptor, (error) => {
  console.log("❌ 默认请求错误:", error);
  return Promise.reject(error);
});

instance.interceptors.response.use(
  commonResponseInterceptor,
  commonErrorInterceptor,
);

// 配置Coze实例的拦截器（使用相同的拦截器函数）
cozeInstance.interceptors.request.use(commonRequestInterceptor, (error) => {
  console.log("❌ Coze请求错误:", error);
  return Promise.reject(error);
});

cozeInstance.interceptors.response.use(
  commonResponseInterceptor,
  commonErrorInterceptor,
);

// 创建聊天专用的请求实例（用于聊天API，指向7002端口）

// 导出实例
export default instance;
export { cozeInstance }; // 导出OSS和Coze专用实例
